import type { BillingWalletUsageStorage } from '@customerio/hydra-ui-api';

// Flex plan stored-profile limit from the credits design. The API does not supply it yet.
export const STORED_PROFILE_CAP_FREE = 1_000;

export type CreditsUsage = {
  emailSpendCents: number;
  emailSends: number;
  activeProfiles: number;
  storedProfiles: number;
};

/** A purchase action (Add funds or Manage storage) and its gating. A disabled button shows the message in a tooltip. */
export type AddFundsAction = {
  onPurchase: () => void;
  isPurchaseDisabled: boolean;
  purchaseDisabledMessage: string;
};

const CRITICAL_RUNWAY_DAYS = 3;
const LOW_RUNWAY_DAYS = 7;

/** `soft`: past the stored-profile limit, new profiles are still stored. `hard`: new profiles are refused. */
export type StorageZone = BillingWalletUsageStorage['zone'];

/** People and objects stored across the account family, against the stored-profile limit. */
export type AccountStorage = {
  stored: number;
  limit: number;
  zone: StorageZone;
};

export type CreditsInput = {
  balanceCents: number;
  hasFunded: boolean;
  dailySpendCents: number;
  activeProfiles: number;
  freeActiveProfiles: number | null;
};

export type CreditsState =
  | { kind: 'free' }
  | { kind: 'free-active-limit' }
  | { kind: 'healthy' }
  | { kind: 'low'; daysLeft: number }
  | { kind: 'critical'; daysLeft: number }
  | { kind: 'empty' };

export type CreditsTone = 'default' | 'warn' | 'critical';

export type CreditsPresentation = {
  tone: CreditsTone;
  subLabel: string | null;
};

function runwayDays(balanceCents: number, dailySpendCents: number) {
  if (dailySpendCents <= 0) return null;
  // Round up, so the tone agrees with the number of days that shows.
  return Math.ceil(balanceCents / dailySpendCents);
}

export function deriveCreditsState(input: CreditsInput): CreditsState {
  if (!input.hasFunded) {
    const atActiveLimit = input.freeActiveProfiles != null && input.activeProfiles >= input.freeActiveProfiles;
    return atActiveLimit ? { kind: 'free-active-limit' } : { kind: 'free' };
  }

  if (input.balanceCents <= 0) return { kind: 'empty' };

  const daysLeft = runwayDays(input.balanceCents, input.dailySpendCents);
  if (daysLeft == null || daysLeft > LOW_RUNWAY_DAYS) return { kind: 'healthy' };
  if (daysLeft <= CRITICAL_RUNWAY_DAYS) return { kind: 'critical', daysLeft };
  return { kind: 'low', daysLeft };
}

/** `unfunded`: never added funds. `empty`: added funds, balance is 0. `funded`: balance above 0. */
export type FundsStatus = 'unfunded' | 'empty' | 'funded';

export function fundsStatus(state: CreditsState): FundsStatus {
  switch (state.kind) {
    case 'free':
    case 'free-active-limit':
      return 'unfunded';
    case 'empty':
      return 'empty';
    case 'healthy':
    case 'low':
    case 'critical':
      return 'funded';
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

export function presentCreditsState(state: CreditsState): CreditsPresentation {
  switch (state.kind) {
    case 'free':
      return { tone: 'default', subLabel: null };
    case 'free-active-limit':
      return { tone: 'critical', subLabel: 'Limit reached' };
    case 'healthy':
      return { tone: 'default', subLabel: null };
    case 'low':
      return { tone: 'warn', subLabel: 'Low balance' };
    case 'critical':
      return { tone: 'critical', subLabel: 'Very low balance' };
    case 'empty':
      return { tone: 'critical', subLabel: 'Not delivering' };
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}
